import React, { useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styles from "./StreamingText.module.css";

// In-memory map for message tracking
const messageIdMap = new Map();

const StreamingText = ({
  text = "",
  speed = 10,
  className = "",
  enableMarkdown = true,
  isRealStream = false, // When true, animates text as it arrives from server
  onAnimationComplete = null, // Callback when streaming animation finishes
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);

  // Refs for non-real-stream animation
  const indexRef = useRef(0);
  const startTimeRef = useRef(null);
  const animationRef = useRef(null);

  // Refs for real-stream animation
  const targetTextRef = useRef(text);
  const displayedLengthRef = useRef(0);
  const streamAnimationRef = useRef(null);
  const animationStartTimeRef = useRef(null);
  const lastTextChangeTimeRef = useRef(Date.now());

  // Always generate or reuse a timestamp for this exact text
  const messageId = useRef(
    messageIdMap.has(text)
      ? messageIdMap.get(text)
      : (() => {
          const ts = Date.now().toString();
          messageIdMap.set(text, ts);
          return ts;
        })()
  );

  // Generate a hash key based on text + generated timestamp
  const hashText = (value) =>
    btoa(unescape(encodeURIComponent(value))).slice(0, 30);
  const hashKey = hashText(`${messageId.current}::${text}`);

  const getStreamedHashes = () => {
    try {
      const raw = sessionStorage.getItem("streamed_hashes");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  };

  const markTextHashAsStreamed = (hash) => {
    const hashes = getStreamedHashes();
    hashes.add(hash);
    sessionStorage.setItem(
      "streamed_hashes",
      JSON.stringify(Array.from(hashes))
    );
  };

  // Stable callback for animation completion
  const handleAnimationComplete = useCallback(() => {
    setIsComplete(true);
    if (onAnimationComplete) {
      onAnimationComplete();
    }
  }, [onAnimationComplete]);

  // Handle real-time streaming with smooth character animation
  useEffect(() => {
    if (!isRealStream) return;

    // Update target text when new chunks arrive
    targetTextRef.current = text;
    lastTextChangeTimeRef.current = Date.now();

    // Animation function - animates characters at a steady rate
    const animateStream = () => {
      const now = Date.now();
      const targetLength = targetTextRef.current.length;
      const currentLength = displayedLengthRef.current;

      // Initialize start time on first frame
      if (animationStartTimeRef.current === null) {
        animationStartTimeRef.current = now;
      }

      if (currentLength < targetLength) {
        // Calculate how many characters should be displayed based on elapsed time
        const elapsed = now - animationStartTimeRef.current;
        const expectedLength = Math.floor(elapsed / speed);

        // Show at least current + 1, but cap at target length
        const newLength = Math.min(
          Math.max(currentLength + 1, expectedLength),
          targetLength
        );

        if (newLength > currentLength) {
          displayedLengthRef.current = newLength;
          setDisplayedText(targetTextRef.current.slice(0, newLength));
        }

        // Continue animation
        streamAnimationRef.current = requestAnimationFrame(animateStream);
      } else {
        // We've displayed all text, check if more is coming
        const timeSinceLastChange = now - lastTextChangeTimeRef.current;

        if (timeSinceLastChange > 500) {
          // Text hasn't changed for 500ms, streaming is complete
          handleAnimationComplete();
          streamAnimationRef.current = null;
        } else {
          // Keep checking in case more text arrives
          streamAnimationRef.current = requestAnimationFrame(animateStream);
        }
      }
    };

    // Start animation if not already running
    if (!streamAnimationRef.current) {
      streamAnimationRef.current = requestAnimationFrame(animateStream);
    }

    return () => {
      // Don't cancel animation on text updates - let it continue
    };
  }, [text, isRealStream, speed, handleAnimationComplete]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (streamAnimationRef.current) {
        cancelAnimationFrame(streamAnimationRef.current);
        streamAnimationRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Handle non-real-stream animation (original behavior)
  useEffect(() => {
    if (isRealStream) return;

    const hashes = getStreamedHashes();
    if (hashes.has(hashKey)) {
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }

    setDisplayedText("");
    setIsComplete(false);
    indexRef.current = 0;
    startTimeRef.current = null;

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const expectedIndex = Math.floor(elapsed / speed);

      if (expectedIndex >= text.length) {
        setDisplayedText(text);
        setIsComplete(true);
        markTextHashAsStreamed(hashKey);
        cancelAnimationFrame(animationRef.current);
        return;
      }

      if (expectedIndex > indexRef.current) {
        // For markdown, show a bit more text to avoid cutting in middle of syntax
        const displayLength = enableMarkdown ?
          Math.min(expectedIndex + 50, text.length) : expectedIndex;
        setDisplayedText(text.slice(0, displayLength));
        indexRef.current = expectedIndex;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationRef.current);
  }, [text, speed, hashKey, isRealStream]);

  // Render with markdown support
  if (enableMarkdown) {
    return (
      <div className={className}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayedText}</ReactMarkdown>
        {!isComplete && (
          <FontAwesomeIcon icon={faCircle} className={styles.blink_dot} />
        )}
      </div>
    );
  }

  // Render streaming text without markdown (for partial content)
  const fadedSpanCount = 5;
  const normalText = isComplete
    ? displayedText
    : displayedText.slice(0, -fadedSpanCount);
  const fadedChars = isComplete
    ? []
    : displayedText
        .slice(-fadedSpanCount)
        .split("")
        .map((char, i) => (
          <span key={i} style={{ opacity: (i + 1) / fadedSpanCount }}>
            {char}
          </span>
        ));

  return (
    <p className={className}>
      {normalText}
      {fadedChars}
      {!isComplete && (
        <FontAwesomeIcon icon={faCircle} className={styles.blink_dot} />
      )}
    </p>
  );
};

export default StreamingText;
